import { useState, useMemo } from "react";
import { getCatEmoji } from "../utils/nlp";
import { checkAlerts } from "../utils/notifications";
import { getBizType, getMomoProvider, THEME, fm, today, dayName, daysAgo } from "../utils/helpers";

export default function Dashboard({ config, transactions, onBack, onReset }) {
  const [period, setPeriod] = useState("today");
  const [selEmp, setSelEmp] = useState(null);
  const [tab, setTab] = useState("overview");
  const biz = getBizType(config.businessType);

  const pTxns = useMemo(() => transactions.filter((t) => {
    if (period === "today") return t.date === today();
    if (period === "week") return t.date >= daysAgo(7);
    if (period === "month") return t.date >= daysAgo(30);
    return true;
  }), [transactions, period]);

  const tv = pTxns.filter((t) => t.type === "vente").reduce((s, t) => s + t.amount, 0);
  const td = pTxns.filter((t) => t.type === "depense").reduce((s, t) => s + t.amount, 0);
  const benef = tv - td;

  const workers = useMemo(() => {
    const list = config.employees.length > 0 ? config.employees : [{ id: "owner", name: config.ownerName, role: "Patron", avatar: "👤" }];
    return list.map((e) => {
      const et = pTxns.filter((t) => t.employeeId === e.id);
      const v = et.filter((t) => t.type === "vente").reduce((s, t) => s + t.amount, 0);
      const d = et.filter((t) => t.type === "depense").reduce((s, t) => s + t.amount, 0);
      return { ...e, v, d, b: v - d, txns: et, n: et.length };
    }).sort((a, b) => b.b - a.b);
  }, [config, pTxns]);

  // Daily chart
  const dailyData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = daysAgo(i);
      const dt = transactions.filter((t) => t.date === d);
      days.push({
        date: d,
        v: dt.filter((t) => t.type === "vente").reduce((s, t) => s + t.amount, 0),
        d: dt.filter((t) => t.type === "depense").reduce((s, t) => s + t.amount, 0),
        label: dayName(d).split(" ").slice(0, 2).join(" "),
      });
    }
    return days;
  }, [transactions]);
  const maxDaily = Math.max(...dailyData.map((d) => Math.max(d.v, d.d)), 1);

  // Categories
  const cats = useMemo(() => {
    const m = {};
    pTxns.forEach((t) => {
      if (!m[t.category]) m[t.category] = { v: 0, d: 0 };
      m[t.category][t.type === "vente" ? "v" : "d"] += t.amount;
    });
    return Object.entries(m).sort((a, b) => (b[1].v + b[1].d) - (a[1].v + a[1].d)).slice(0, 8);
  }, [pTxns]);

  // Alerts
  const alerts = useMemo(() => checkAlerts({
    totalVentes: tv, totalDepenses: td, benefice: benef,
    nbVentes: pTxns.filter((t) => t.type === "vente").length,
    nbTransactions: pTxns.length,
  }, config), [tv, td, benef, pTxns, config]);

  // ─── Employee detail ───
  if (selEmp) {
    const w = workers.find((e) => e.id === selEmp);
    if (!w) { setSelEmp(null); return null; }
    return (
      <div style={{ minHeight: "100vh", background: THEME.bg }}>
        <div style={{ background: `linear-gradient(135deg, ${biz.color}, ${biz.color}bb)`, padding: "20px 16px", color: "#fff", borderRadius: "0 0 24px 24px" }}>
          <button onClick={() => setSelEmp(null)} style={backBtn}>← Retour</button>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: 18, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>{w.avatar}</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{w.name}</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>{w.role} • {w.n} transactions</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 20, marginTop: 16 }}>
            {[{ l: "Ventes", v: w.v }, { l: "Dépenses", v: w.d }, { l: "Bénéfice", v: w.b }].map((s, i) => (
              <div key={i}><div style={{ fontSize: 10, opacity: 0.6, textTransform: "uppercase", letterSpacing: 1 }}>{s.l}</div><div style={{ fontSize: 18, fontWeight: 900 }}>{fm(s.v)} F</div></div>
            ))}
          </div>
        </div>
        <div style={{ padding: "16px 14px" }}>
          <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${THEME.border}` }}>
            {w.txns.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#ddd", fontSize: 13 }}>Aucune transaction</div>}
            {w.txns.map((tx, i) => (
              <TxnRow key={tx.id} tx={tx} last={i === w.txns.length - 1} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main dashboard ───
  return (
    <div style={{ minHeight: "100vh", background: THEME.bg }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(150deg, ${biz.color} 0%, ${biz.color}cc 100%)`,
        padding: "16px 16px 20px", color: "#fff",
        borderRadius: "0 0 28px 28px",
        boxShadow: `0 8px 30px ${biz.color}33`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <button onClick={onBack} style={backBtn}>← Accueil</button>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{dayName(today())}</div>
        </div>
        <div style={{ fontSize: 11, opacity: 0.6, textTransform: "uppercase", letterSpacing: 2 }}>Bénéfice net</div>
        <div style={{ fontSize: 36, fontWeight: 900, marginTop: 2 }}>
          {fm(benef)} <span style={{ fontSize: 14, opacity: 0.6 }}>FCFA</span>
        </div>
        <div style={{ display: "flex", gap: 20, marginTop: 12 }}>
          {[{ l: "💵 Ventes", v: tv }, { l: "💸 Dépenses", v: td }, { l: "📝 Txns", v: pTxns.length }].map((s, i) => (
            <div key={i}><div style={{ fontSize: 10, opacity: 0.6 }}>{s.l}</div><div style={{ fontSize: 16, fontWeight: 800 }}>{i < 2 ? `${fm(s.v)} F` : s.v}</div></div>
          ))}
        </div>
      </div>

      <div style={{ padding: "14px 14px 80px" }}>
        {/* Period */}
        <div style={{ display: "flex", gap: 5, marginBottom: 12, overflowX: "auto" }}>
          {[{ k: "today", l: "Aujourd'hui" }, { k: "week", l: "7 jours" }, { k: "month", l: "30 jours" }, { k: "all", l: "Tout" }].map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} style={{
              background: period === p.k ? biz.color : "#fff",
              color: period === p.k ? "#fff" : "#888",
              border: `1.5px solid ${period === p.k ? biz.color : THEME.border}`,
              borderRadius: 20, padding: "6px 14px", fontSize: 12,
              fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
            }}>{p.l}</button>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", background: "#F0ECE4", borderRadius: 12, padding: 3, marginBottom: 14 }}>
          {[{ k: "overview", l: "📊 Global" }, { k: "team", l: "👥 Équipe" }, { k: "history", l: "🕐 Historique" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              flex: 1, padding: "8px 0", borderRadius: 10,
              background: tab === t.k ? "#fff" : "transparent",
              border: "none", fontSize: 12, fontWeight: 700,
              cursor: "pointer", color: tab === t.k ? THEME.text : "#999",
              boxShadow: tab === t.k ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
            }}>{t.l}</button>
          ))}
        </div>

        {/* Alerts */}
        {tab === "overview" && alerts.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {alerts.slice(0, 2).map((a, i) => (
              <div key={i} style={{
                background: a.type === "danger" ? "#FEE2E2" : a.type === "warning" ? "#FEF3C7" : a.type === "success" ? "#DCFCE7" : "#E0F2FE",
                border: `1px solid ${a.type === "danger" ? "#EF444433" : a.type === "warning" ? "#F59E0B33" : a.type === "success" ? "#22C55E33" : "#0EA5E933"}`,
                borderRadius: 12, padding: "10px 14px", marginBottom: 6,
                display: "flex", alignItems: "center", gap: 10, fontSize: 12,
              }}>
                <span style={{ fontSize: 18 }}>{a.icon}</span>
                <div><div style={{ fontWeight: 700 }}>{a.title}</div><div style={{ color: "#666", marginTop: 2 }}>{a.message}</div></div>
              </div>
            ))}
          </div>
        )}

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            {/* Chart */}
            <Card title="📈 Évolution (7 jours)">
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100 }}>
                {dailyData.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <div style={{ width: "80%", borderRadius: "4px 4px 0 0", height: Math.max(3, (d.v / maxDaily) * 70), background: THEME.green, transition: "height 0.4s ease" }} />
                    <div style={{ width: "80%", borderRadius: "0 0 4px 4px", height: Math.max(3, (d.d / maxDaily) * 70), background: THEME.red, opacity: 0.5, transition: "height 0.4s ease" }} />
                    <div style={{ fontSize: 9, color: "#bbb", marginTop: 2 }}>{d.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 10 }}>
                <Legend color={THEME.green} label="Ventes" />
                <Legend color={THEME.red} label="Dépenses" opacity={0.5} />
              </div>
            </Card>

            {/* Categories */}
            {cats.length > 0 && (
              <Card title="📦 Top catégories">
                {cats.map(([cat, vals], i) => {
                  const total = vals.v + vals.d;
                  const maxCat = Math.max(...cats.map(([, v]) => v.v + v.d), 1);
                  return (
                    <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < cats.length - 1 ? `1px solid ${THEME.border}` : "none" }}>
                      <span style={{ fontSize: 18 }}>{getCatEmoji(cat)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, textTransform: "capitalize" }}>{cat}</span>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>{fm(total)} F</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 3, background: "#f0ede8", marginTop: 4 }}>
                          <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(90deg, ${biz.color}, ${biz.color}88)`, width: `${(total / maxCat) * 100}%`, transition: "width 0.4s ease" }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Mobile Money */}
            {config.momoProvider && (() => {
              const momo = getMomoProvider(config.momoProvider);
              return momo ? (
                <div style={{
                  background: `${momo.color}10`, border: `1px solid ${momo.color}30`,
                  borderRadius: 16, padding: 14, marginBottom: 14,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>📱 {momo.label}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>N° {config.momoNumber} • Recettes: {fm(tv)} FCFA</div>
                  <div style={{ marginTop: 10, background: momo.color, color: "#fff", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 700, textAlign: "center", cursor: "pointer" }}>
                    Envoyer le bilan par SMS 📤
                  </div>
                </div>
              ) : null;
            })()}
          </>
        )}

        {/* TEAM */}
        {tab === "team" && (
          <>
            {workers.length > 1 && (
              <Card title="🏆 Classement">
                {workers.map((w, i) => {
                  const maxB = Math.max(...workers.map((e) => Math.abs(e.b)), 1);
                  return (
                    <div key={w.id} onClick={() => setSelEmp(w.id)} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 0", borderBottom: i < workers.length - 1 ? `1px solid ${THEME.border}` : "none",
                      cursor: "pointer",
                    }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: 8,
                        background: i === 0 ? "#FEF3C7" : i === 1 ? "#F3F4F6" : "#FFF",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900,
                      }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</div>
                      <span style={{ fontSize: 20 }}>{w.avatar}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{w.name}</div>
                        <div style={{ height: 5, borderRadius: 3, background: "#f0ede8", marginTop: 4 }}>
                          <div style={{ height: "100%", borderRadius: 3, width: `${(Math.abs(w.b) / maxB) * 100}%`, background: w.b >= 0 ? THEME.green : THEME.red, transition: "width 0.4s ease" }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: w.b >= 0 ? THEME.green : THEME.red, minWidth: 75, textAlign: "right" }}>
                        {w.b >= 0 ? "+" : ""}{fm(w.b)} F
                      </div>
                      <span style={{ color: "#ddd" }}>›</span>
                    </div>
                  );
                })}
              </Card>
            )}
            {workers.map((w) => (
              <div key={w.id} onClick={() => setSelEmp(w.id)} style={{
                background: "#fff", borderRadius: 16, padding: "12px 14px",
                marginBottom: 8, cursor: "pointer", border: `1px solid ${THEME.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 14, background: `${biz.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{w.avatar}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{w.name}</div>
                    <div style={{ fontSize: 11, color: THEME.textSub }}>{w.role} • {w.n} txns</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: w.b >= 0 ? THEME.green : THEME.red }}>{w.b >= 0 ? "+" : ""}{fm(w.b)} F</div>
                    <div style={{ display: "flex", gap: 8, fontSize: 10, justifyContent: "flex-end", marginTop: 2 }}>
                      <span style={{ color: THEME.green, fontWeight: 700 }}>↑{fm(w.v)}</span>
                      <span style={{ color: THEME.red, fontWeight: 700 }}>↓{fm(w.d)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${THEME.border}` }}>
            {pTxns.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#ddd", fontSize: 13 }}>Aucune transaction</div>}
            {pTxns.slice(0, 50).map((tx, i) => (
              <TxnRow key={tx.id} tx={tx} showName last={i === Math.min(pTxns.length, 50) - 1} />
            ))}
          </div>
        )}

        {/* Reset */}
        <button onClick={() => {
          if (window.confirm("Tout effacer ? Cette action est irréversible !")) onReset();
        }} style={{
          width: "100%", marginTop: 24, padding: 14,
          background: "#fff", border: `1.5px solid #FEE2E2`,
          borderRadius: 14, fontSize: 13, fontWeight: 700,
          color: THEME.red, cursor: "pointer",
        }}>🗑️ Réinitialiser toutes les données</button>
      </div>
    </div>
  );
}

// ─── Reusable parts ───
function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: 14, marginBottom: 14, border: `1px solid ${THEME.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function Legend({ color, label, opacity = 1 }) {
  return (
    <span style={{ fontSize: 10, color: "#888", display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, opacity, display: "inline-block" }} /> {label}
    </span>
  );
}

function TxnRow({ tx, showName = false, last = false }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", borderBottom: last ? "none" : `1px solid ${THEME.border}`,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: tx.type === "vente" ? "#DCFCE7" : "#FEE2E2",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
      }}>{getCatEmoji(tx.category)}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          <span style={{ textTransform: "capitalize" }}>{tx.category}</span>
          {showName && <span style={{ color: THEME.textSub, fontWeight: 500 }}> — {tx.employeeName}</span>}
        </div>
        <div style={{ fontSize: 10, color: THEME.textSub }}>{tx.time} • {dayName(tx.date)}</div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: tx.type === "vente" ? THEME.green : THEME.red }}>
        {tx.type === "vente" ? "+" : "-"}{fm(tx.amount)} F
      </div>
    </div>
  );
}

const backBtn = {
  background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
  borderRadius: 10, padding: "6px 14px", fontSize: 13,
  cursor: "pointer", fontWeight: 700,
};
